import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, MousePointerClick, Activity, Calendar } from 'lucide-react';
import { API } from '@/config/api';

const RANGE_OPTIONS = [7, 14, 30, 60, 90];

function Bar({ label, value, max }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="text-sm">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-medium truncate mr-2">{label}</span>
        <span className="text-xs text-muted-foreground">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary/40 overflow-hidden">
        <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/analytics/overview?days=${days}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then(setData)
      .finally(() => setLoading(false));
  }, [days]);

  const topSections = data?.top_sections || [];
  const topActions = data?.top_actions || [];
  const dau = data?.daily_active_users || [];
  const maxSection = topSections[0]?.count || 1;
  const maxAction = topActions[0]?.count || 1;
  const maxDau = Math.max(1, ...dau.map((d) => d.users));

  return (
    <Card className="p-5 glass-card" data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">User Analytics</h2>
          {data && <span className="text-xs text-muted-foreground">{data.total_events.toLocaleString()} events</span>}
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="analytics-range-pills">
          {RANGE_OPTIONS.map((d) => (
            <Button
              key={d}
              size="sm"
              variant={d === days ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              className="h-7 px-2 text-xs"
              data-testid={`range-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div data-testid="top-sections">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1">
                <Activity className="h-3 w-3" /> Top sections visited
              </h3>
              {topSections.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No data yet — events will populate after users navigate.</p>
              ) : (
                <div className="space-y-2.5">
                  {topSections.map((s) => <Bar key={s.section} label={s.section} value={s.count} max={maxSection} />)}
                </div>
              )}
            </div>
            <div data-testid="top-actions">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1">
                <MousePointerClick className="h-3 w-3" /> Top features used
              </h3>
              {topActions.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No data yet — events will populate after users interact.</p>
              ) : (
                <div className="space-y-2.5">
                  {topActions.map((a) => <Bar key={a.action} label={a.action} value={a.count} max={maxAction} />)}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border" data-testid="daily-active-users">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Daily active users
            </h3>
            {dau.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No activity in this window.</p>
            ) : (
              <div className="flex items-end gap-1 h-24" title="DAU per day">
                {dau.map((d) => {
                  const h = Math.max(4, Math.round((d.users / maxDau) * 100));
                  return (
                    <div
                      key={d.date}
                      className="flex-1 min-w-[6px] bg-primary/60 rounded-t hover:bg-primary transition-colors"
                      style={{ height: `${h}%` }}
                      title={`${d.date}: ${d.users} unique users`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
